import {
  Flex,
  Box,
  Text,
  HStack,
  Tooltip,
  useBreakpointValue
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faArrowLeft,
  faArrowDownToBracket,
  faCopy,
  faCalendarDay,
  faFileLines,
  faBookSparkles
} from '@fortawesome/pro-regular-svg-icons';
import {
  Badge,
  ButtonIcon,
  ButtonText,
  Link,
  Modal,
  useToast
} from '@morressier/alembic';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { FormFieldAuthors } from '@publishing-engine/domain/types/article';
import { useMemo, useState } from 'react';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { formatDate } from 'src/utils/date';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import { downloadFile } from 'src/utils/file';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import TextWithIcon from 'src/components/TextWithIcon';
import { formatAuthorFull, formatAuthorLocation } from 'src/utils/formatAuthor';
import ContactJournal from 'src/components/ContactPublisher/ContactPublisher';
import ContactJournalHeader from 'src/components/ContactPublisher/ContactPublisherHeader';
import { useLocation, useNavigate } from 'react-router-dom';
import { DividerVertical } from 'src/components/Submission/OverviewTab';
const MAX_VISIBLE = 1;

interface ArticleHeaderProps {
  onClose: () => void;
}

const JournalHeader = ({ onClose }: ArticleHeaderProps) => {
  const toast = useToast();
  const { getFieldState } = useSubmissionFields('submission');
  const Authors = getFieldState<FormFieldAuthors>('Authors');
  const {
    submissionId,
    workflowResponse: { workflowData, manuscriptId, cloneSource },
    fieldsResponse,
    submissionContextId,
    submissionContext
  } = useSubmissionData();
  const navigate = useNavigate();
  const location = useLocation();
  const submissionType = fieldsResponse.articleTypeName;
  const title = fieldsResponse.fields.Title!.value as string;
  const submissionDate = workflowData.states?.find(state =>
    ['initial', 'transferOffered'].includes(state.name)
  )?.completedWith?.date;
  const handleCopy = async (variable: string) => {
    await navigator.clipboard.writeText(variable);
    toast({
      variant: 'success',
      title: 'Copied to clipboard',
      description: '',
      isClosable: true
    });
  };
  const modalWidth = useBreakpointValue({ base: '95vw', md: '720px' });
  const [showAll, setShowAll] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const authors = useMemo(
    () => (Array.isArray(Authors?.value) ? Authors.value : []),
    [Authors]
  );
  const manuscriptUrl = (
    fieldsResponse?.fields?.Manuscript?.value as ISubmissionFile
  )?.url;

  const titlePageUrl = (
    fieldsResponse?.fields?.TitlePage?.value as ISubmissionFile
  )?.url;
  const selectedFocusIssue = submissionContext?.data?.focusIssues?.find(
    item => item.id === fieldsResponse?.fields?.FocusIssue?.value
  );
  const showClonedBadge = !!cloneSource?.previousSubmissionId;

  const remainingCount = authors.length - MAX_VISIBLE;
  const shouldShowToggle = authors.length > MAX_VISIBLE;
  const currentLocationState =
    (location.state as { from?: string; parentFrom?: string } | null) ?? null;

  const handleOpenClonedFromSubmission = () => {
    if (!cloneSource?.previousSubmissionId) {
      return;
    }

    navigate(
      `/app/my-submissions/${cloneSource.previousSubmissionId}${
        location.search || '?submissionType=my-submissions-active'
      }`,
      {
        state: {
          from: location.pathname + location.search,
          parentFrom: currentLocationState?.from
        }
      }
    );
  };

  return (
    <>
      <Box p={4} bg="white" border="1px solid" borderColor="gray.200">
        <Flex
          width={'100%'}
          justifyContent={'space-between'}
          flexDirection={{ base: 'column', xl: 'row' }}
          gap={2}
        >
          <Flex direction={'column'} minWidth={0} overflow="hidden">
            <HStack spacing={1} my={1}>
              <Box>
                <ButtonText onClick={onClose}>
                  <FontAwesomeIcon icon={faArrowLeft} size="lg" /> Back
                </ButtonText>
              </Box>
              <DividerVertical />
              <Text as="span" textStyle={'headingItem'} fontWeight="semibold">
                # {manuscriptId}
              </Text>
              {/* Contact Publisher */}
              <ContactJournalHeader
                setIsContactModalOpen={setIsContactModalOpen}
              />
            </HStack>

            <Box textStyle="headingItem" mb={2} noOfLines={1}>
              <RichTextPreview value={title} />
            </Box>
            <Flex justifyContent={'space-between'}>
              <Flex wrap="wrap" align="center" textStyle={'textContext'}>
                <HStack spacing={1} mr={3}>
                  <TextWithIcon
                    icon={faCalendarDay}
                    text={formatDate(new Date(submissionDate ?? ''))}
                  />
                </HStack>
                <HStack spacing={1} mr={3}>
                  <TextWithIcon icon={faFileLines} text={submissionType} />
                </HStack>
                {selectedFocusIssue && (
                  <Flex
                    flexDirection="row"
                    alignContent="center"
                    gap={2}
                    mr={2}
                  >
                    <TextWithIcon
                      icon={faBookSparkles}
                      text={'Special Issue: '}
                    />
                    <Tooltip
                      label={selectedFocusIssue.name}
                      hasArrow
                      placement="top"
                    >
                      <Box maxW="220px" minW="0">
                        {selectedFocusIssue?.issueLink ||
                        submissionContext?.data?.guidelineLink ? (
                          <Link
                            variant="primary"
                            href={
                              selectedFocusIssue?.issueLink ||
                              submissionContext?.data?.guidelineLink
                            }
                            target="_blank"
                            style={{ display: 'block' }}
                          >
                            <Text textStyle="textContext" isTruncated>
                              {selectedFocusIssue.name}
                            </Text>
                          </Link>
                        ) : (
                          <Text textStyle="textContext" isTruncated>
                            {selectedFocusIssue.name}
                          </Text>
                        )}
                      </Box>
                    </Tooltip>
                  </Flex>
                )}
                {manuscriptUrl && (
                  <HStack spacing={1}>
                    <ButtonText
                      leftIcon={
                        <FontAwesomeIcon
                          icon={faArrowDownToBracket}
                        ></FontAwesomeIcon>
                      }
                      fontSize={'sm'}
                      onClick={() => {
                        downloadFile(manuscriptUrl);
                      }}
                    >
                      Manuscript
                    </ButtonText>
                  </HStack>
                )}
                {titlePageUrl && (
                  <HStack spacing={1} marginLeft={2}>
                    <ButtonText
                      leftIcon={
                        <FontAwesomeIcon
                          icon={faArrowDownToBracket}
                        ></FontAwesomeIcon>
                      }
                      fontSize={'sm'}
                      onClick={() => {
                        downloadFile(titlePageUrl);
                      }}
                    >
                      Title page
                    </ButtonText>
                  </HStack>
                )}
              </Flex>
            </Flex>

            <Flex wrap="wrap" align="center" alignItems={'center'}>
              {authors.length >= 1 && (
                <Flex align="center" minWidth={0}>
                  <TextWithIcon icon={faUser} />
                  <Tooltip
                    label={formatAuthorFull(authors[0]!)}
                    isDisabled={!formatAuthorLocation(authors[0]!)}
                    hasArrow
                    placement="top"
                  >
                    <Text isTruncated textStyle="textContext">
                      <Text as="span" fontWeight="500">
                        {`${authors[0]?.firstName} ${authors[0]?.lastName}`}
                      </Text>
                      {formatAuthorLocation(authors[0]!)
                        ? `, ${formatAuthorLocation(authors[0]!)}`
                        : ''}
                    </Text>
                  </Tooltip>
                  <ButtonIcon
                    variant="primary"
                    ariaLabel="copytoclipboard"
                    icon={<FontAwesomeIcon icon={faCopy} />}
                    size="small"
                    onClick={() => handleCopy(formatAuthorFull(authors[0]!))}
                  />
                </Flex>
              )}

              {shouldShowToggle && (
                <ButtonText
                  variant="outline"
                  fontSize={'sm'}
                  onClick={() => setShowAll(prev => !prev)}
                >
                  {showAll
                    ? 'Show Less'
                    : `+${remainingCount} author${
                        remainingCount > 1 ? 's' : ''
                      }`}
                </ButtonText>
              )}
            </Flex>

            {showClonedBadge && (
              <Flex alignItems="center" gap={2} flexWrap="wrap" mt={2}>
                <Badge size="context" variant="neutral">
                  Cloned from:
                </Badge>
                <Link
                  textStyle="textContext"
                  variant="primary"
                  onClick={handleOpenClonedFromSubmission}
                >
                  {cloneSource?.previousManuscriptId}
                </Link>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Box>
      <Modal
        isOpen={showAll}
        width={modalWidth}
        onClose={() => setShowAll(false)}
      >
        <Flex padding={4} flexDirection="column" gap={4}>
          <Text textStyle="headingGroup">Authors</Text>
          <Flex flexDirection="column">
            {authors.map((author, idx) => (
              <Flex key={idx} align="center" gap={1}>
                <Tooltip
                  label={formatAuthorFull(author)}
                  isDisabled={!formatAuthorLocation(author)}
                  hasArrow
                  placement="top"
                >
                  <Text isTruncated textStyle="textContext">
                    <Text as="span" fontWeight="500">
                      {`${author.firstName} ${author.lastName}`}
                    </Text>
                    {formatAuthorLocation(author)
                      ? `, ${formatAuthorLocation(author)}`
                      : ''}
                  </Text>
                </Tooltip>
                <ButtonIcon
                  variant="primary"
                  ariaLabel="copytoclipboard"
                  icon={<FontAwesomeIcon icon={faCopy} />}
                  size="small"
                  onClick={() => handleCopy(formatAuthorFull(author))}
                />
              </Flex>
            ))}
          </Flex>
        </Flex>
      </Modal>
      {manuscriptId && (
        <Modal
          width={modalWidth}
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
        >
          <ContactJournal
            submissionId={submissionId}
            submissionContextId={submissionContextId}
            manuscriptId={manuscriptId}
            setIsContactModalOpen={setIsContactModalOpen}
          />
        </Modal>
      )}
    </>
  );
};

export default JournalHeader;
